import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plane,
  Star,
  Baby,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  MapPin,
  Users,
  Car,
  Bus,
  Phone,
  CreditCard,
  Award,
  Zap,
  Heart,
  Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BookingWidget from "../components/booking/BookingWidget";
import Header from "../components/layout/Header";
import heroImage from "../assets/airport.webp";
import atOrly2 from "../assets/atOrly2.webp";
import atBva3 from "../assets/atBva3.webp";
import atCdg1 from "../assets/atCdg1.webp";

const AirportTransfers = () => {
  return (
    <div className="min-h-screen" data-testid="airport-transfers-page">
      <Header />

      {/* Hero Section - Matching Home Page Structure */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-24 pb-16 lg:pt-32 lg:pb-20">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Airport Terminal"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight-navy/80 via-midnight-navy/50 to-midnight-navy/30"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-champagne-gold/20 text-champagne-gold rounded-full text-sm font-medium mb-4 lg:mb-6">
                <Plane className="w-4 h-4" />
                All Paris Airports · CDG · Orly · Beauvais
              </span>

              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-4 lg:mb-6">
                Paris Airport{" "}
                <span className="text-champagne-gold">Private Transfers</span>
              </h1>

              <p className="text-base md:text-lg text-paris-stone/90 mb-6 leading-relaxed max-w-xl">
                Skip the taxi queues. Professional private transfers from CDG,
                Orly & Beauvais airports. Flight tracking, meet & greet service,
                fixed prices & instant confirmation.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 md:gap-4 mb-6">
                <div className="flex items-center gap-2 text-white text-sm">
                  <Eye className="w-4 h-4 text-champagne-gold" />
                  <span>Flight Tracking</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm">
                  <MapPin className="w-4 h-4 text-champagne-gold" />
                  <span>Meet & Greet</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm">
                  <Shield className="w-4 h-4 text-champagne-gold" />
                  <span>Fixed Prices</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 lg:gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-6 py-5 lg:px-8 lg:py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-base lg:text-lg"
                  data-testid="airport-hero-book-btn"
                >
                  <Link to="/booking">
                    Book Airport Transfer
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-6 py-5 lg:px-8 lg:py-6 rounded-full transition-all text-base lg:text-lg"
                >
                  <Link to="/pricing">View All Prices</Link>
                </Button>
              </div>

              {/* Urgency Microcopy */}
              <div className="mt-6 flex items-center gap-4 text-sm">
                <span className="flex items-center gap-2 text-green-400">
                  <Zap className="w-4 h-4" />
                  Instant confirmation
                </span>
                <span className="flex items-center gap-2 text-paris-stone/70">
                  <Clock className="w-4 h-4" />
                  60 min free wait time
                </span>
              </div>

              {/* Phone CTA */}
              <div className="mt-6 lg:mt-8 flex items-center gap-3">
                <a
                  href="tel:+33763519524"
                  className="flex items-center gap-2 text-paris-stone hover:text-champagne-gold transition-colors"
                >
                  <div className="p-2 bg-champagne-gold/20 rounded-full">
                    <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-champagne-gold" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm text-paris-stone/70">
                      Call us 24/7
                    </p>
                    <p className="font-semibold text-white text-sm lg:text-base">
                      +33 763 519 524
                    </p>
                  </div>
                </a>
              </div>
            </motion.div>

            {/* Booking Widget - Desktop */}
            <div className="hidden lg:block py-8">
              <BookingWidget variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Booking Widget */}
      <section className="lg:hidden px-4 py-8 bg-paris-stone relative z-20">
        <BookingWidget variant="hero" />
      </section>

      {/* Social Proof Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                4.9/5
              </p>
              <div className="flex gap-1 justify-center my-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-champagne-gold text-champagne-gold"
                  />
                ))}
              </div>
              <p className="text-sm text-slate-600"> Reviews</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                1500+
              </p>
              <p className="text-sm text-slate-600">Airport Transfers</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                99%
              </p>
              <p className="text-sm text-slate-600">On-Time Pickups</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                60 min
              </p>
              <p className="text-sm text-slate-600">Free Wait Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600">
              Stress-free airport pickup in 3 simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                title: "Book Online",
                desc: "Select airport, destination & flight details. Get instant confirmation.",
                icon: CheckCircle,
              },
              {
                step: "2",
                title: "We Track Your Flight",
                desc: "We monitor your flight. Driver adjusts pickup time automatically.",
                icon: Eye,
              },
              {
                step: "3",
                title: "Meet & Greet",
                desc: "Driver waits at arrivals with your name sign. 60 min free waiting.",
                icon: MapPin,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-champagne-gold rounded-full flex items-center justify-center mx-auto mb-4 text-midnight-navy font-bold text-2xl">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Airports We Serve */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Paris Airports We Serve
            </h2>
            <p className="text-lg text-slate-600">
              Fixed prices to all destinations · No hidden fees
            </p>
          </motion.div>

          {/* Airport Cards Grid */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Charles de Gaulle (CDG)",
                desc: "International airport, 25km northeast of Paris",
                prices: [
                  { to: "Paris City", price: 80 },
                  { to: "Disneyland", price: 70 },
                  { to: "La Defense", price: 90 },
                ],
                popular: true,
                image: atCdg1,
              },
              {
                name: "Paris Orly (ORY)",
                desc: "Second largest airport, 13km south of Paris",
                prices: [
                  { to: "Paris City", price: 80 },
                  { to: "Disneyland", price: 80 },
                  { to: "Versailles", price: 75 },
                ],
                image: atOrly2,
              },
              {
                name: "Beauvais-Tillé (BVA)",
                desc: "Budget airline hub, 85km north of Paris",
                prices: [
                  { to: "Paris City", price: 155 },
                  { to: "Disneyland", price: 155 },
                  { to: "CDG Airport", price: 150 },
                ],
                image: atBva3,
              },
            ].map((airport, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl overflow-hidden shadow-soft hover:shadow-strong transition-all duration-300 group border-2 ${
                  airport.popular
                    ? "bg-champagne-gold/10 border-champagne-gold"
                    : "bg-white border-slate-200"
                }`}
              >
                {/* Top Image */}
                <div className="relative w-full h-48 md:h-56 lg:h-64 overflow-hidden">
                  <img
                    src={airport.image}
                    alt={airport.image}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Popular Badge */}
                  {airport.popular && (
                    <span className="absolute top-4 left-4 px-3 py-1 bg-champagne-gold text-midnight-navy font-bold text-xs rounded shadow-md">
                      MOST POPULAR
                    </span>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <div className="w-14 h-14 bg-eiffel-blue/10 rounded-xl flex items-center justify-center mb-4">
                    <Plane className="w-7 h-7 text-eiffel-blue" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                    {airport.name}
                  </h3>
                  <p className="text-slate-600 mb-4 text-sm">{airport.desc}</p>

                  {/* Prices */}
                  <div className="space-y-2 text-sm border-t pt-4">
                    {airport.prices.map((route, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-slate-600">To {route.to}</span>
                        <span className="font-bold text-midnight-navy">
                          €{route.price}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Book Button */}
                  <Button
                    asChild
                    className="w-full mt-4 bg-midnight-navy hover:bg-midnight-navy/90 text-white rounded-full group-hover:bg-champagne-gold group-hover:text-midnight-navy transition-all"
                  >
                    <Link
                      to={`/booking?pickup=${encodeURIComponent(airport.name)}`}
                    >
                      Book from {airport.name.split(" ")[0]}
                    </Link>
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Showcase */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Our Premium Fleet
            </h2>
            <p className="text-lg text-slate-600">
              Clean, comfortable & spacious vehicles
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-eiffel-blue/10 rounded-xl flex items-center justify-center">
                  <Car className="w-8 h-8 text-eiffel-blue" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-midnight-navy">
                    Sedan Car
                  </h3>
                  <p className="text-slate-600">1-3 passengers</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Mercedes
                  E-Class or similar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> 2 checked +
                  2 hand luggage
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Free child
                  seats on request
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Meet &
                  greet at arrivals
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-champagne-gold/10 rounded-xl flex items-center justify-center">
                  <Bus className="w-8 h-8 text-champagne-gold" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-midnight-navy">
                    Spacious Van
                  </h3>
                  <p className="text-slate-600">4-8 passengers</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Mercedes
                  V-Class or similar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> 8 checked +
                  8 hand luggage
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Perfect for
                  families & groups
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Extra
                  legroom & comfort
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Why Choose Our Airport Transfers?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Eye,
                title: "Flight Tracking",
                desc: "We monitor your flight and adjust pickup time automatically",
              },
              {
                icon: Clock,
                title: "60 Min Free Wait",
                desc: "No stress if customs or baggage takes longer than expected",
              },
              {
                icon: CreditCard,
                title: "No Hidden Fees",
                desc: "Fixed prices include all tolls, parking & meet & greet",
              },
              {
                icon: Award,
                title: "Professional Service",
                desc: "Licensed drivers, clean vehicles, 24/7 support",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 bg-champagne-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-champagne-gold" />
                </div>
                <h3 className="font-heading font-bold text-lg text-midnight-navy mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              What Travelers Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Michael R.",
                text: "Flight was delayed by 2 hours but driver was still there waiting! No extra charge. Excellent service from landing to hotel.",
                rating: 5,
              },
              {
                name: "Sophie K.",
                text: "So much better than waiting for a taxi. Driver met us at arrivals with a sign, helped with luggage. Very professional.",
                rating: 5,
              },
              {
                name: "David L.",
                text: "Used them for Beauvais which is far from Paris. Fixed price was great - no surprises. Driver was punctual and friendly.",
                rating: 5,
              },
            ].map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-soft"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-champagne-gold text-champagne-gold"
                    />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 italic">"{review.text}"</p>
                <p className="font-semibold text-midnight-navy">
                  {review.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "What if my flight is delayed?",
                a: "We track all flights automatically and adjust pickup times accordingly. There's no extra charge for flight delays - we include 60 minutes of free waiting time.",
              },
              {
                q: "How will I find my driver?",
                a: "Your driver will be waiting in the arrivals hall with a sign showing your name or at the pick-up point. You'll also receive the releavant details including the driver details & contact number before pickup.",
              },
              {
                q: "Are there any hidden fees?",
                a: "No. Our prices are all-inclusive. Tolls, parking, meet & greet, and 60 min waiting time are all included. The price you see is the price you pay.",
              },
              {
                q: "Can I book a return transfer?",
                a: "Yes! Select 'Round Trip' when booking to get both airport pickup and return transfer in one booking.",
              },
              {
                q: "What payment methods do you accept?",
                a: "You can pay by cash or card directly to your driver upon arrival. No online payment required.",
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-paris-stone rounded-xl p-6"
              >
                <h3 className="font-semibold text-midnight-navy mb-2">
                  {faq.q}
                </h3>
                <p className="text-slate-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-midnight-navy">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Arrive in Style
            </h2>
            <p className="text-paris-stone/80 mb-6 max-w-xl mx-auto">
              Book your airport transfer now. Flight tracking, meet & greet, and
              60 min free waiting included.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 py-6 rounded-full shadow-lg"
                data-testid="airport-cta-book-btn"
              >
                <Link to="/booking">
                  Book Now - From €70
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-8 py-6 rounded-full"
              >
                <a href="tel:+33763519524">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sticky CTA for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
        <Button
          asChild
          className="w-full bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold py-4 rounded-full"
        >
          <Link to="/booking">
            Book Your Airport Transfer
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>

      <div className="lg:hidden h-20"></div>
    </div>
  );
};

export default AirportTransfers;
